import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import { useAuthContext } from '@repo/hooks';
import { XIcon } from '@repo/icons';
import { setUserMetadataFlag } from '@repo/redux/actions/user';
import { useTheme } from '@repo/themes';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import { useLocation } from 'react-router-dom';
import styles from './ImportingContacts.module.scss';
import { shuffleArray } from '@repo/utils/helpers/array';

const Flower = () => {
  const [theme] = useTheme();
  const { pathname } = useLocation();

  let bgStroke = 'rgb(64,64,64)';
  let squigglerStroke = '#FFF';

  if (theme === 'light' && pathname !== '/onboarding') {
    bgStroke = 'rgb(220,220,220)';
    squigglerStroke = '#666';
  }

  return (
    <svg
      className={styles.flower}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 50 54"
      width="50"
      height="54"
    >
      <defs>
        <path
          id="flowerGraphic"
          fill="none"
          d="M17.4 27c0-.36-.18-.7-.48-.9a10.56 10.56 0 0 0-11.12-.35 10.53 10.53 0 0 0-3.9 14.38 10.53 10.53 0 0 0 14.4 3.8 10.56 10.56 0 0 0 5.27-9.8 1.1 1.1 0 0 0-.56-.86c-.31-.18-.7-.2-1.02-.05a10.56 10.56 0 0 0-5.86 9.47c0 5.83 4.7 10.56 10.5 10.56s10.5-4.73 10.5-10.56c0-4.15-2.38-7.74-5.85-9.46-.32-.16-.7-.14-1.02.04-.32.18-.53.5-.56.87a10.56 10.56 0 0 0 5.27 9.8 10.53 10.53 0 0 0 14.4-3.81 10.53 10.53 0 0 0-3.9-14.38c-3.6-2.07-7.9-1.8-11.12.34-.3.2-.48.55-.48.91s.18.71.48.9a10.56 10.56 0 0 0 11.12.35 10.53 10.53 0 0 0 3.9-14.38 10.53 10.53 0 0 0-14.4-3.8 10.56 10.56 0 0 0-5.27 9.8c.03.36.25.68.56.86.31.18.7.2 1.02.05a10.56 10.56 0 0 0 5.85-9.47c0-5.83-4.7-10.56-10.5-10.56s-10.5 4.73-10.5 10.56c0 4.15 2.4 7.74 5.86 9.47.32.16.7.13 1.02-.05.32-.18.53-.5.56-.87a10.56 10.56 0 0 0-5.27-9.8 10.53 10.53 0 0 0-14.4 3.81 10.53 10.53 0 0 0 3.9 14.38c3.6 2.08 7.9 1.8 11.12-.34.3-.2.47-.55.47-.91Z"
        />
        <mask id="flowerMask">
          <rect x="0" y="0" width="50" height="54" fill="black" />
          <use href="#flowerGraphic" stroke="#fff" strokeWidth="0.8" />
        </mask>
      </defs>
      <g mask="url(#flowerMask)">
        <use href="#flowerGraphic" stroke={bgStroke} strokeWidth="1" />
        <use
          className={styles.leadLine}
          stroke={squigglerStroke}
          href="#flowerGraphic"
          strokeWidth="0.7"
          strokeDasharray="160 544"
        />
        <use
          className={styles.followLine}
          stroke={bgStroke}
          href="#flowerGraphic"
          strokeWidth="1"
          strokeDasharray="352 352"
        />
      </g>
    </svg>
  );
};

const messageList = [
  'Importing your network',
  'Analyzing your relationships',
  'Deduplicating across integrations',
  'Syncing across integrations',
  'Enriching all profiles',
  'Gathering the latest news',
  'Building individual timelines',
  'Calculating relationship strength',
  'Finding all related people',
  'Indexing people search',
];

export default function ImportingContactsAlert({
  className,
  dismissable,
}: {
  className?: string;
  dismissable?: boolean;
}) {
  const dispatch = useDispatch();
  const { pollForAccountStatus } = useAuthContext();
  const [messageIdx, setMessageIdx] = useState(0);
  const messages = useMemo(() => shuffleArray(messageList), []);

  const messagesByIdx = useMemo(() => {
    return messages.reduce(
      (acc, message, idx) => ({
        ...acc,
        [String(idx)]: <span key={idx}>{message}</span>,
      }),
      {},
    );
  }, []);

  useEffect(() => {
    pollForAccountStatus();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIdx((prev) => {
        const next = prev + 1;
        return next < messages.length ? next : 0;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className={cn(styles.container, className)}>
      <Flower />
      <h3>
        <StateBasedFadeTransition
          state={String(messageIdx)}
          elementByStateMap={messagesByIdx}
          duration="medium"
        />
      </h3>
      <p>
        We’ll let you know as soon as we’re ready. While we work, feel free to explore our{' '}
        <a href="https://library.clay.earth" target="_blank">
          library&nbsp;→
        </a>
      </p>
      {dismissable && (
        <button
          className={styles.dismissBtn}
          onClick={() => dispatch(setUserMetadataFlag('accountProcessingToastDismissed', true))}
        >
          <XIcon width="10" height="10" />
        </button>
      )}
    </div>
  );
}
